import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminFormFieldError,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminConfigLockedField,
  AdminSupportingText,
  AdminHeaderAction,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminFormDetailTextareaClasses,
  adminFormTwoColGridClass,
  AdminFormDropdownChevron,
  AdminCompactFormPage,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_PAGE_TITLE_CLASS,
} from '../shared/adminUx';
import {
  AUTOMATION_MODULES,
  AUTOMATION_ROUTE,
  CONDITION_OPERATOR_OPTIONS,
  RETRY_ATTEMPT_OPTIONS,
  RETRY_INTERVAL_OPTIONS,
  applyAutomationSelectionChange,
  buildAutomationScopeKey,
  buildSampleAutomationRules,
  createEmptyAutomationForm,
  createEmptyCondition,
  formatAutomationDateTime,
  formatConditionsPreview,
  formatConnectedConfigPreview,
  formatRetryPreview,
  formatSchedulePreview,
  getAutomatedJobLabel,
  getAutomatedJobMeaning,
  getAutomatedJobReference,
  getAutomatedJobsForScope,
  getAvailableConditionFields,
  getAutomationModuleLabel,
  getConditionFieldLabel,
  getConditionValueLabel,
  getConditionValueOptions,
  getConnectedConfigLabel,
  getConnectedConfigReference,
  getConnectedConfigsForJob,
  getRecordTypeLabel,
  getRecordTypesForModule,
  getRetryAttemptsLabel,
  getRetryIntervalLabel,
  getScheduleLabel,
  getScheduleMeaning,
  getScheduleOptionsForJob,
  jobRequiresConnectedConfig,
  jobRequiresSchedule,
  jobSupportsConditions,
  jobSupportsRetry,
  normalizeAutomationForm,
  operatorIsMulti,
} from '../../../data/automationConfig';
import {
  canAddAutomationRule,
  canEditAutomationRule,
  canSaveAutomationRule,
  canViewAutomationConfiguration,
  getEditRuleSource,
  getPublishedRule,
  saveAutomationRule,
} from '../../../data/automationRuleWorkflow';
import { matchesUrlName } from '../../../utils/slugify';

function emptyForm() {
  return {
    ...createEmptyAutomationForm(),
    version: 1,
    pendingDraft: null,
    configurationHistory: [],
    testHistory: [],
  };
}

function mergeIncomingRule(raw, ruleIdParam) {
  const normalized = normalizeAutomationForm(raw || {});
  return {
    ...emptyForm(),
    ...normalized,
    id: normalized.id ?? ruleIdParam ?? '',
    version: raw?.version ?? 1,
    pendingDraft: raw?.pendingDraft ?? null,
    configurationHistory: raw?.configurationHistory ?? [],
    testHistory: raw?.testHistory ?? [],
  };
}

const TextValue = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    {mono ? (
      <p className="text-offblack text-sm py-2 font-mono break-words">{value || '—'}</p>
    ) : (
      <AdminDetailFieldValue className="py-2 break-words whitespace-pre-wrap">{value || '—'}</AdminDetailFieldValue>
    )}
  </div>
);

const withCurrentOption = (options, value, getLabel) => {
  if (!value || options.some((option) => option.value === value)) return options;
  return [...options, { value, label: `${getLabel(value) || value} (Unavailable)` }];
};

const AutomationRuleDetail = () => {
  const navigate = useNavigate();
  const { ruleName } = useParams();
  const location = useLocation();
  const { auth } = useAuth();
  const isCreate = location.pathname.endsWith('/add');
  const isEdit = location.pathname.includes('/edit');
  const isView = !isCreate && !isEdit;

  const canView = canViewAutomationConfiguration(auth);
  const canAdd = canAddAutomationRule(auth);
  const canEdit = canEditAutomationRule(auth);
  const canSave = canSaveAutomationRule(auth);

  const ruleFromState = location.state?.rule;
  const existingRulesFromState = location.state?.existingRules;

  const allRules = useMemo(
    () => existingRulesFromState ?? buildSampleAutomationRules(),
    [existingRulesFromState],
  );

  const publishedBaseline = useMemo(() => {
    if (isCreate) return null;
    if (ruleFromState?.id) {
      const fromList = allRules.find((rule) => rule.id === ruleFromState.id);
      return getPublishedRule(fromList || ruleFromState);
    }
    if (ruleName) {
      const found = allRules.find((rule) => matchesUrlName(rule.ruleName, ruleName));
      return getPublishedRule(found || null);
    }
    return null;
  }, [isCreate, ruleFromState, ruleName, allRules]);

  const resolvedSource = useMemo(() => {
    if (ruleFromState && isView) {
      const foundRecord = allRules.find((rule) => rule.id === ruleFromState.id) || ruleFromState;
      return mergeIncomingRule(getPublishedRule(foundRecord) || foundRecord, foundRecord.id);
    }
    if (ruleFromState) {
      const foundRecord = allRules.find((rule) => rule.id === ruleFromState.id) || ruleFromState;
      const source = isEdit ? getEditRuleSource(foundRecord) : getPublishedRule(foundRecord) || foundRecord;
      return mergeIncomingRule(source, foundRecord.id);
    }
    if (!isCreate && ruleName) {
      const found = allRules.find((rule) => matchesUrlName(rule.ruleName, ruleName));
      if (found) {
        const source = isEdit ? getEditRuleSource(found) : getPublishedRule(found);
        return mergeIncomingRule(source, found.id);
      }
    }
    return emptyForm();
  }, [ruleFromState, isCreate, isEdit, isView, ruleName, allRules]);

  const [formData, setFormData] = useState(resolvedSource);
  const [errors, setErrors] = useState({});

  const scopeLocked = Boolean(!isCreate && formData.id);
  const recordTypeOptions = useMemo(
    () => withCurrentOption(getRecordTypesForModule(formData.module), formData.recordType, getRecordTypeLabel),
    [formData.module, formData.recordType],
  );
  const jobOptions = useMemo(
    () => withCurrentOption(
      getAutomatedJobsForScope(formData.module, formData.recordType).map((job) => ({
        value: job.value,
        label: job.label,
      })),
      formData.automatedJob,
      getAutomatedJobLabel,
    ),
    [formData.module, formData.recordType, formData.automatedJob],
  );
  const connectedConfigOptions = useMemo(
    () => withCurrentOption(
      getConnectedConfigsForJob(formData.automatedJob).map((config) => ({
        value: config.value,
        label: config.label,
      })),
      formData.connectedConfig,
      getConnectedConfigLabel,
    ),
    [formData.automatedJob, formData.connectedConfig],
  );
  const scheduleOptions = useMemo(
    () => withCurrentOption(getScheduleOptionsForJob(formData.automatedJob), formData.schedule, getScheduleLabel),
    [formData.automatedJob, formData.schedule],
  );
  const conditionFields = useMemo(() => getAvailableConditionFields(formData), [formData]);
  const canAddCondition = jobSupportsConditions(formData.automatedJob) && conditionFields.length > 0;
  const showConnected = jobRequiresConnectedConfig(formData.automatedJob);
  const showSchedule = jobRequiresSchedule(formData.automatedJob);
  const showRetry = jobSupportsRetry(formData.automatedJob);
  const showRetryInterval = showRetry && Number(formData.maxRetryAttempts) > 0;
  const scheduleLocked = scheduleOptions.length === 1;
  const conditionsPreview = formatConditionsPreview(formData.conditions, formData.automatedJob);

  useEffect(() => {
    setFormData(resolvedSource);
  }, [resolvedSource]);

  const handleChange = (field, value) => {
    setErrors((prev) => {
      const nextErr = {
        ...prev,
        [field]: undefined,
        duplicate: undefined,
        scope: undefined,
        concurrent: undefined,
        form: undefined,
        permission: undefined,
      };
      if (field === 'conditions') {
        Object.keys(nextErr).forEach((key) => {
          if (key.startsWith('condition_')) delete nextErr[key];
        });
      }
      return nextErr;
    });
    setFormData((prev) => applyAutomationSelectionChange(prev, field, value));
  };

  const updateCondition = (index, patch) => {
    setErrors((prev) => ({ ...prev, [`condition_${index}`]: undefined, conditions: undefined }));
    setFormData((prev) => {
      const conditions = [...(prev.conditions || [])];
      conditions[index] = { ...conditions[index], ...patch };
      if (patch.field) conditions[index].values = [];
      if (patch.operator && !operatorIsMulti(patch.operator)) {
        conditions[index].values = (conditions[index].values || []).slice(0, 1);
      }
      return { ...prev, conditions };
    });
  };

  const handleSave = () => {
    if (isCreate && !canAdd) {
      setErrors({ permission: 'You do not have permission to save this Rule.' });
      return;
    }
    if (isEdit && !canEdit) {
      setErrors({ permission: 'You do not have permission to save this Rule.' });
      return;
    }
    const result = saveAutomationRule(allRules, formData, publishedBaseline || {}, {
      originalLastUpdated: resolvedSource.lastUpdated,
      originalScopeKey: publishedBaseline ? buildAutomationScopeKey(publishedBaseline) : undefined,
      canSave,
    });
    if (!result.success) {
      setErrors({
        ...result.errors,
        form: result.summary?.length ? result.summary.join(' ') : 'Fix the highlighted fields.',
      });
      return;
    }
    navigate(AUTOMATION_ROUTE, { state: { rules: result.rules } });
  };

  const renderSelect = (name, label, options, placeholder, disabledExtra = false, required = true, emptyMessage = 'No compatible choices available') => {
    if (isView) {
      const selected = options.find((option) => option.value === formData[name]);
      return <TextValue label={label} value={selected?.label || formData[name] || '—'} />;
    }
    const emptyCompatible = !disabledExtra && !options.length;
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>
          {label} {required && <span className="text-alertred">*</span>}
        </AdminDetailFieldLabel>
        <div className="relative w-full min-w-0">
          <select
            id={`aut-${name}-select`}
            value={formData[name] || ''}
            title={options.find((option) => option.value === formData[name])?.label || placeholder}
            onChange={(e) => handleChange(name, e.target.value)}
            disabled={disabledExtra || emptyCompatible}
            className={adminFormDetailSelectClasses(disabledExtra || emptyCompatible, Boolean(errors[name]))}
          >
            <option value="">{emptyCompatible ? emptyMessage : placeholder}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <AdminFormDropdownChevron />
        </div>
        {errors[name] && <AdminFormFieldError>{errors[name]}</AdminFormFieldError>}
      </div>
    );
  };

  if (!canView && isView) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Automation Rule Detail</AdminPageTitle>
        <AdminFormFieldError>You do not have permission to view this Rule.</AdminFormFieldError>
      </AdminLayout>
    );
  }

  if (isView) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Automation Rule Detail</AdminPageTitle>
        <div className={`space-y-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className={`${adminFormTwoColGridClass} gap-y-4`}>
              <TextValue label="Rule ID" value={formData.id} mono />
              <TextValue label="Rule Name" value={formData.ruleName} />
              <TextValue label="Module" value={getAutomationModuleLabel(formData.module)} />
              <TextValue label="Record Type" value={getRecordTypeLabel(formData.recordType)} />
              <div className="sm:col-span-2">
                <TextValue label="Automated Job" value={getAutomatedJobLabel(formData.automatedJob)} />
              </div>
              <div className="sm:col-span-2">
                <TextValue label="Job meaning" value={getAutomatedJobMeaning(formData.automatedJob) || '—'} />
              </div>
              <TextValue label="Job reference" value={getAutomatedJobReference(formData.automatedJob) || '—'} mono />
              <TextValue label="Connected Config" value={formatConnectedConfigPreview(formData)} />
              <TextValue
                label="Config reference"
                value={showConnected ? (getConnectedConfigReference(formData.connectedConfig) || '—') : '—'}
                mono
              />
              <TextValue label="Schedule" value={formatSchedulePreview(formData)} />
              {showSchedule && formData.schedule ? (
                <div className="sm:col-span-2">
                  <TextValue label="Schedule meaning" value={getScheduleMeaning(formData.schedule) || '—'} />
                </div>
              ) : null}
              <TextValue label="Retry" value={formatRetryPreview(formData)} />
              {showRetry ? (
                <>
                  <TextValue
                    label="Maximum Retry Attempts"
                    value={getRetryAttemptsLabel(formData.maxRetryAttempts) || formData.maxRetryAttempts || '—'}
                  />
                  <TextValue
                    label="Retry Interval"
                    value={
                      showRetryInterval
                        ? (getRetryIntervalLabel(formData.retryInterval) || formData.retryInterval || '—')
                        : 'Not Applicable'
                    }
                  />
                </>
              ) : null}
              <div className="sm:col-span-2">
                <TextValue
                  label="Conditions"
                  value={conditionsPreview.lines.length ? conditionsPreview.lines.join('\n') : conditionsPreview.text}
                />
              </div>
              <TextValue label="Date Added" value={formatAutomationDateTime(formData.dateAdded)} />
              <TextValue label="Added By" value={formData.addedBy} />
              <TextValue label="Last Updated" value={formatAutomationDateTime(formData.lastUpdated)} />
              <TextValue label="Last Updated By" value={formData.lastUpdatedBy} />
            </div>
          </DashboardMainContainer>
        </div>
      </AdminLayout>
    );
  }

  const hasValidationErrors = Boolean(
    errors.permission
    || errors.checksUnavailable
    || errors.duplicate
    || errors.scope
    || errors.concurrent
    || errors.form
    || errors.conditions,
  );

  return (
    <AdminLayout>
      <AdminCompactFormPage
        title={isCreate ? 'Add Automation Rule' : 'Edit Automation Rule'}
        onSubmit={handleSave}
        showSave={false}
      >
        {formData.id ? (
          <AdminConfigLockedField label="Rule ID" value={formData.id} mono isView={false} />
        ) : null}
        <div className="space-y-1 w-full min-w-0">
          <AdminDetailFieldLabel>
            Rule Name <span className="text-alertred">*</span>
          </AdminDetailFieldLabel>
          <input
            id="aut-rule-name"
            type="text"
            placeholder="Enter rule name"
            value={formData.ruleName || ''}
            onChange={(e) => handleChange('ruleName', e.target.value)}
            className={adminFormDetailInputClasses(false, Boolean(errors.ruleName))}
          />
          {errors.ruleName ? <AdminFormFieldError>{errors.ruleName}</AdminFormFieldError> : null}
        </div>

        {scopeLocked ? (
          <>
            <AdminConfigLockedField label="Module" value={getAutomationModuleLabel(formData.module)} isView={false} />
            <AdminConfigLockedField label="Record Type" value={getRecordTypeLabel(formData.recordType)} isView={false} />
            <AdminConfigLockedField label="Automated Job" value={getAutomatedJobLabel(formData.automatedJob)} isView={false} />
            <AdminConfigLockedField
              label="Connected Config"
              value={formatConnectedConfigPreview(formData)}
              isView={false}
            />
          </>
        ) : (
          <>
            {renderSelect('module', 'Module', AUTOMATION_MODULES, 'Select module')}
            {renderSelect(
              'recordType',
              'Record Type',
              recordTypeOptions,
              formData.module ? 'Select record type' : 'Select module first',
              !formData.module,
            )}
            {renderSelect(
              'automatedJob',
              'Automated Job',
              jobOptions,
              formData.recordType ? 'Select automated job' : 'Select record type first',
              !formData.recordType,
            )}
            {showConnected ? (
              connectedConfigOptions.length === 1 ? (
                <AdminConfigLockedField
                  label="Connected Config"
                  value={getConnectedConfigLabel(formData.connectedConfig) || '—'}
                  isView={false}
                />
              ) : (
                renderSelect(
                  'connectedConfig',
                  'Connected Config',
                  connectedConfigOptions,
                  formData.automatedJob ? 'Select connected config' : 'Select automated job first',
                  !formData.automatedJob,
                )
              )
            ) : formData.automatedJob ? (
              <AdminConfigLockedField label="Connected Config" value="Not Required" isView={false} />
            ) : null}
          </>
        )}

        {formData.automatedJob ? (
          <AdminConfigLockedField
            label="Job meaning"
            value={getAutomatedJobMeaning(formData.automatedJob) || '—'}
            isView={false}
          />
        ) : null}

        {showSchedule ? (
          scheduleLocked ? (
            <AdminConfigLockedField label="Schedule" value={getScheduleLabel(formData.schedule) || '—'} isView={false} />
          ) : (
            renderSelect(
              'schedule',
              'Schedule',
              scheduleOptions,
              formData.automatedJob ? 'Select schedule' : 'Select automated job first',
              !formData.automatedJob,
            )
          )
        ) : formData.automatedJob ? (
          <AdminConfigLockedField label="Schedule" value="Not Applicable" isView={false} />
        ) : null}

        {showSchedule && formData.schedule ? (
          <AdminConfigLockedField
            label="Schedule meaning"
            value={getScheduleMeaning(formData.schedule) || '—'}
            isView={false}
          />
        ) : null}

        {showRetry ? (
          <>
            {renderSelect(
              'maxRetryAttempts',
              'Maximum Retry Attempts',
              RETRY_ATTEMPT_OPTIONS,
              'Select maximum retry attempts',
            )}
            {showRetryInterval ? (
              renderSelect('retryInterval', 'Retry Interval', RETRY_INTERVAL_OPTIONS, 'Select retry interval')
            ) : (
              <AdminConfigLockedField label="Retry Interval" value="Not Applicable" isView={false} />
            )}
          </>
        ) : formData.automatedJob ? (
          <>
            <AdminConfigLockedField label="Maximum Retry Attempts" value="Not Applicable" isView={false} />
            <AdminConfigLockedField label="Retry Interval" value="Not Applicable" isView={false} />
          </>
        ) : null}

        <div className="space-y-3 w-full min-w-0">
          <AdminDetailFieldLabel>Optional Conditions</AdminDetailFieldLabel>
          {!jobSupportsConditions(formData.automatedJob) && formData.automatedJob ? (
            <AdminDetailFieldValue className="py-2">Not Applicable</AdminDetailFieldValue>
          ) : (formData.conditions || []).length ? (
            <div className="space-y-3">
              {(formData.conditions || []).map((condition, index) => {
                const valueOptions = withCurrentOption(
                  getConditionValueOptions(condition.field),
                  (condition.values || [])[0],
                  (value) => getConditionValueLabel(condition.field, value),
                );
                const multi = operatorIsMulti(condition.operator);
                return (
                  <div key={condition.id || index} className="rounded-xl border border-lightgray p-4 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <AdminSupportingText className="mb-0 font-semibold !text-offblack">Condition</AdminSupportingText>
                      <button
                        type="button"
                        onClick={() => handleChange(
                          'conditions',
                          (formData.conditions || []).filter((_, conditionIndex) => conditionIndex !== index),
                        )}
                        className="shrink-0 inline-flex items-center justify-center leading-none text-offblack hover:text-alertred"
                        aria-label="Remove Condition"
                      >
                        <i className="fi fi-rs-trash text-sm" aria-hidden />
                      </button>
                    </div>
                    {errors[`condition_${index}`] ? (
                      <AdminFormFieldError>{errors[`condition_${index}`]}</AdminFormFieldError>
                    ) : null}
                    <div className="space-y-1 w-full min-w-0">
                      <AdminDetailFieldLabel>Field <span className="text-alertred">*</span></AdminDetailFieldLabel>
                      <div className="relative w-full min-w-0">
                        <select
                          value={condition.field || ''}
                          onChange={(e) => updateCondition(index, { field: e.target.value })}
                          className={adminFormDetailSelectClasses(false, Boolean(errors[`condition_${index}`]))}
                        >
                          <option value="">Select field</option>
                          {withCurrentOption(conditionFields, condition.field, getConditionFieldLabel).map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <AdminFormDropdownChevron />
                      </div>
                    </div>
                    <div className="space-y-1 w-full min-w-0">
                      <AdminDetailFieldLabel>Operator <span className="text-alertred">*</span></AdminDetailFieldLabel>
                      <div className="relative w-full min-w-0">
                        <select
                          value={condition.operator || ''}
                          onChange={(e) => updateCondition(index, { operator: e.target.value })}
                          disabled={!condition.field}
                          className={adminFormDetailSelectClasses(!condition.field)}
                        >
                          <option value="">Select operator</option>
                          {CONDITION_OPERATOR_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <AdminFormDropdownChevron />
                      </div>
                    </div>
                    {multi ? null : (
                      <div className="space-y-1 w-full min-w-0">
                        <AdminDetailFieldLabel>Value <span className="text-alertred">*</span></AdminDetailFieldLabel>
                        <div className="relative w-full min-w-0">
                          <select
                            value={(condition.values || [])[0] || ''}
                            onChange={(e) => updateCondition(index, { values: e.target.value ? [e.target.value] : [] })}
                            disabled={!condition.field || !valueOptions.length}
                            className={adminFormDetailSelectClasses(!condition.field || !valueOptions.length)}
                          >
                            <option value="">
                              {!condition.field
                                ? 'Select field first'
                                : valueOptions.length
                                  ? 'Select value'
                                  : 'No compatible values available'}
                            </option>
                            {valueOptions.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                          <AdminFormDropdownChevron />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <AdminDetailFieldValue className="py-2">
              {canAddCondition ? 'No Conditions' : 'No compatible Conditions available'}
            </AdminDetailFieldValue>
          )}
          {errors.conditions ? <AdminFormFieldError>{errors.conditions}</AdminFormFieldError> : null}
          {canAddCondition ? (
            <button
              type="button"
              onClick={() => handleChange('conditions', [...(formData.conditions || []), createEmptyCondition()])}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primaryblue hover:text-brightblue"
            >
              <i className="fi fi-rs-plus text-xs" aria-hidden />
              Add Condition
            </button>
          ) : null}
        </div>

        <div className="space-y-1 w-full min-w-0">
          <AdminDetailFieldLabel>
            Change Reason <span className="text-alertred">*</span>
          </AdminDetailFieldLabel>
          <textarea
            id="aut-change-reason"
            placeholder="Enter a change reason"
            value={formData.changeReason || ''}
            onChange={(e) => handleChange('changeReason', e.target.value)}
            className={adminFormDetailTextareaClasses(false, Boolean(errors.changeReason))}
            rows={3}
          />
          {errors.changeReason ? <AdminFormFieldError>{errors.changeReason}</AdminFormFieldError> : null}
        </div>

        {hasValidationErrors ? (
          <div className="space-y-2">
            {errors.permission ? <AdminFormFieldError>{errors.permission}</AdminFormFieldError> : null}
            {errors.checksUnavailable ? <AdminFormFieldError>{errors.checksUnavailable}</AdminFormFieldError> : null}
            {errors.duplicate ? <AdminFormFieldError>{errors.duplicate}</AdminFormFieldError> : null}
            {errors.scope ? <AdminFormFieldError>{errors.scope}</AdminFormFieldError> : null}
            {errors.concurrent ? <AdminFormFieldError>{errors.concurrent}</AdminFormFieldError> : null}
            {errors.form ? <AdminFormFieldError>{errors.form}</AdminFormFieldError> : null}
            {errors.conditions ? <AdminFormFieldError>{errors.conditions}</AdminFormFieldError> : null}
          </div>
        ) : null}

        <div className="flex items-center justify-end">
          <AdminHeaderAction type="submit" disabled={!canSave}>Save</AdminHeaderAction>
        </div>
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default AutomationRuleDetail;
